import { OptionsApi } from '@core/swagger-client';
import { Button, Camera, ContainerNoTopSpace, ContainerWebinarInner, ErrorMessagePage, Heading, LoaderDots, Microphone, Network } from '@core/ui';
import { useQuery } from '@tanstack/react-query';
import { lazy, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AudioAndVideoSelection } from '../../organism/AudioAndVideoSelection';
const Broadcast = lazy(() => import('./broadcast'));
const Player = lazy(() => import('./player'));

export const TestSuitePage = () => {
    const { t } = useTranslation();
    const [startTest, setStartTest] = useState(false);

    // Handle the browser back button to return to explanation view
    useEffect(() => {
        const handlePopState = () => {
            setAudioDeviceId(null);
            setVideoDeviceId(null);
            setStartTest(false);
            setTestEnded(false);
        };

        globalThis.window.addEventListener('popstate', handlePopState);
        return () => globalThis.window.removeEventListener('popstate', handlePopState);
    }, []);

    const openDeviceSelection = () => {
        // Add a new history entry so back button will revert this state
        globalThis.window.history.pushState({ deviceSelectionOpen: true }, '');
        setStartTest(true);
    };

    const [audioDeviceId, setAudioDeviceId] = useState<string | null>(null);
    const [videoDeviceId, setVideoDeviceId] = useState<string | null>(null);
    const [testEnded, setTestEnded] = useState(false);

    // Don't run on mount, we'll trigger it manually after device selection
    const { data: vindralChannelInformation, error, refetch, isFetching } = useQuery(OptionsApi.testSuite.getTestChannel({ enabled: false }));

    const testDuration = 55;

    useEffect(() => {
        if (!vindralChannelInformation) return;

        let duration = testDuration;

        const interval = setInterval(() => {
            if (duration <= 0) {
                setTestEnded(true);
                clearInterval(interval);
            } else {
                // Looked up per tick: the bar is not rendered while the test channel is being refetched.
                const progressBar = document.getElementById('test-progress-bar') as HTMLProgressElement | null;
                if (progressBar) progressBar.value = testDuration - duration; // Update progress
                duration--;
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [vindralChannelInformation]);

    useEffect(() => {
        // Refresh the test channel when devices change
        if (audioDeviceId && videoDeviceId) {
            refetch().then(() => {
                setTestEnded(false);
            });
        }
    }, [audioDeviceId, videoDeviceId]);

    const retry = () => {
        refetch().then(() => {
            setTestEnded(false);
        });
    };

    if (error) {
        return <ErrorMessagePage apiError={error} errorSubject="TestSuite.ApiErrorCode" />;
    }

    return (
        <ContainerNoTopSpace>
            <ContainerWebinarInner>
                <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                    {t('TestSuite.PageHeading')}
                </Heading>
                <div className="bg-white rounded-lg max-w-480 p-4 z-1">
                    {testEnded && (
                        <div className="flex flex-col max-w-3xl mx-auto pb-4">
                            <div className="flex justify-around px-4 py-8 md:py-12 lg:py-16">
                                <Microphone classNames="size-6 md:size-8 stroke-primary" />
                                <Camera classNames="size-6 md:size-8 stroke-primary" />
                                <Network classNames="size-6 md:size-8 stroke-primary" />
                            </div>
                            <p>{t('TestSuite.Text.TestEnded')}</p>
                            <Button variant="primary" classNames="mx-auto mt-8" onClick={retry}>
                                {t('TestSuite.Button.RetryTest')}
                            </Button>
                        </div>
                    )}
                    {!testEnded && (
                        <>
                            {(!videoDeviceId || !audioDeviceId) && (
                                <>
                                    {!startTest && (
                                        <div className="flex flex-col max-w-3xl mx-auto pb-4">
                                            <div className="flex justify-around px-4 py-8 md:py-12 lg:py-16">
                                                <Microphone classNames="size-6 md:size-8 stroke-primary" />
                                                <Camera classNames="size-6 md:size-8 stroke-primary" />
                                                <Network classNames="size-6 md:size-8 stroke-primary" />
                                            </div>
                                            <p className="mb-4">
                                                <Trans i18nKey="TestSuite.Text.PreTest" />
                                            </p>
                                            <p>
                                                <Trans i18nKey="TestSuite.Text.Disclaimer" components={{ em: <em /> }} />
                                            </p>
                                            <Button variant="primary" classNames="mx-auto mt-8" onClick={openDeviceSelection}>
                                                {t('TestSuite.Button.StartTest')}
                                            </Button>
                                        </div>
                                    )}
                                    {startTest && (
                                        <AudioAndVideoSelection
                                            onAudioVideoSelect={(audioDeviceId, videoDeviceId) => {
                                                setAudioDeviceId(audioDeviceId);
                                                setVideoDeviceId(videoDeviceId);
                                            }}
                                            disableExternalIngest={true}
                                        />
                                    )}
                                </>
                            )}
                            {/* Not while refetching: after back-and-restart the cached channel's token has expired
                                (1-minute booking), and MoQ would report a rejection as "did not connect". */}
                            {videoDeviceId && audioDeviceId && vindralChannelInformation && !isFetching && (
                                <>
                                    <p className="max-w-3xl mt-8 mx-auto mb-8">{t('TestSuite.Text.Explanation')}</p>
                                    <div className="relative flex-1 flex flex-col items-center justify-center mb-8">
                                        <label htmlFor="test-progress-bar" className="mb-2">
                                            {t('TestSuite.Progress.Label')}
                                            <LoaderDots classNames="relative bottom-[-.17em] h-0.75em inline-block fill-current ms-1" />
                                        </label>
                                        <progress id="test-progress-bar" className="progress" value="0" max={testDuration}></progress>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <Broadcast
                                            videoDeviceId={videoDeviceId}
                                            audioDeviceId={audioDeviceId}
                                            vindralChannelInformation={vindralChannelInformation}
                                        />
                                        <Player vindralChannel={vindralChannelInformation} />
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
